import React from 'react';
import './ProductFormByType.css';

/**
 * Dünger-spezifisches Formular
 * Strukturiert in Sektionen für bessere Übersichtlichkeit
 */
function FertilizerForm({ formData, handleChange, handleArrayChange, disabled }) {
  const fertilizerTypes = ['Mineralisch', 'Organisch', 'Organisch-mineralisch'];
  const formulations = ['Flüssig', 'Pulver', 'Granulat'];
  const packageSizes = ['100 ml', '250 ml', '500 ml', '1 L', '2.5 L', '5 L', '10 L', '20 L'];
  const nutrients = ['N', 'P', 'K', 'Ca', 'Mg', 'S', 'Fe', 'Mn', 'Zn', 'Cu', 'B', 'Mo'];
  const phaseSuitability = [
    'Sämlingsphase',
    'Wachstum',
    'Blüte',
    'Spätblüte / Finishing',
    'Universaldünger'
  ];
  const systemCompatibility = ['Erde', 'Kokos', 'Hydro', 'Aeroponik', 'DWC'];
  const applicationFrequencies = [
    'Bei jeder Bewässerung',
    'Wöchentlich',
    'Alle 2 Wochen',
    'Nach Bedarf',
    'Einmalig'
  ];

  const toggleArrayValue = (name, value) => {
    if (!handleArrayChange) return;
    handleArrayChange(name, value);
  };

  return (
    <div className="product-type-form-section">
      <h3>🧪 Dünger-Spezifikationen</h3>

      {/* SECTION 1: Grunddaten */}
      <fieldset className="form-section">
        <legend className="form-section-title">📋 Grunddaten</legend>
        <div className="form-row">
          <div className="form-group">
            <label htmlFor="fertilizerProductLine">Produktlinie</label>
            <input
              type="text"
              id="fertilizerProductLine"
              name="fertilizerProductLine"
              value={formData.fertilizerProductLine}
              onChange={handleChange}
              placeholder="z.B. BioBizz Try-Pack"
              disabled={disabled}
            />
          </div>
          <div className="form-group">
            <label htmlFor="fertilizerType">Typ</label>
            <select
              id="fertilizerType"
              name="fertilizerType"
              value={formData.fertilizerType}
              onChange={handleChange}
              disabled={disabled}
            >
              <option value="">Bitte wählen</option>
              {fertilizerTypes.map(type => (
                <option key={type} value={type}>{type}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label htmlFor="fertilizerFormulation">Darreichungsform</label>
            <select
              id="fertilizerFormulation"
              name="fertilizerFormulation"
              value={formData.fertilizerFormulation}
              onChange={handleChange}
              disabled={disabled}
            >
              <option value="">Bitte wählen</option>
              {formulations.map(type => (
                <option key={type} value={type}>{type}</option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label>Verfügbare Gebindegrößen</label>
            <div className="phase-checkboxes">
              {packageSizes.map(size => (
                <label key={size} className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={formData.fertilizerPackageSizes.includes(size)}
                    onChange={() => toggleArrayValue('fertilizerPackageSizes', size)}
                    disabled={disabled}
                  />
                  <span>{size}</span>
                </label>
              ))}
            </div>
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label htmlFor="fertilizerNutrientScheduleLink">Düngeschema PDF Link</label>
            <input
              type="url"
              id="fertilizerNutrientScheduleLink"
              name="fertilizerNutrientScheduleLink"
              value={formData.fertilizerNutrientScheduleLink}
              onChange={handleChange}
              placeholder="z.B. https://example.com/schedule.pdf"
              disabled={disabled}
            />
          </div>
        </div>
      </fieldset>

      {/* SECTION 2: Zusammensetzung */}
      <fieldset className="form-section">
        <legend className="form-section-title">⚗️ Zusammensetzung</legend>
        <div className="form-row">
          <div className="form-group">
            <label htmlFor="fertilizerNpk">NPK (Text)</label>
            <input
              type="text"
              id="fertilizerNpk"
              name="fertilizerNpk"
              value={formData.fertilizerNpk}
              onChange={handleChange}
              placeholder="z.B. 3-1-4"
              disabled={disabled}
            />
          </div>
        </div>
      </fieldset>

      {/* SECTION 3: Anwendungsgebiete & Kompatibilität */}
      <fieldset className="form-section">
        <legend className="form-section-title">🌱 Anwendungsgebiete & Kompatibilität</legend>
        <div className="form-group full-width">
          <label>Phasenspezifische Eignung</label>
          <div className="phase-checkboxes">
            {phaseSuitability.map(phase => (
              <label key={phase} className="checkbox-label">
                <input
                  type="checkbox"
                  checked={formData.fertilizerPhaseSuitability.includes(phase)}
                  onChange={() => toggleArrayValue('fertilizerPhaseSuitability', phase)}
                  disabled={disabled}
                />
                <span>{phase}</span>
              </label>
            ))}
          </div>
        </div>

        <div className="form-group full-width">
          <label>Anbausystem-Kompatibilität</label>
          <div className="phase-checkboxes">
            {systemCompatibility.map(system => (
              <label key={system} className="checkbox-label">
                <input
                  type="checkbox"
                  checked={formData.fertilizerSystemCompatibility.includes(system)}
                  onChange={() => toggleArrayValue('fertilizerSystemCompatibility', system)}
                  disabled={disabled}
                />
                <span>{system}</span>
              </label>
            ))}
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                name="fertilizerAutoIrrigationSuitable"
                checked={formData.fertilizerAutoIrrigationSuitable}
                onChange={handleChange}
                disabled={disabled}
              />
              <span>Automatische Bewässerung geeignet</span>
            </label>
          </div>
        </div>
      </fieldset>

      {/* SECTION 4: Parameter & Dosierung */}
      <fieldset className="form-section">
        <legend className="form-section-title">📊 Parameter & Dosierung</legend>
        <div className="form-row">
          <div className="form-group">
            <label htmlFor="fertilizerDosage">Dosierung (ml pro Liter Wasser)</label>
            <input
              type="text"
              id="fertilizerDosage"
              name="fertilizerDosage"
              value={formData.fertilizerDosage}
              onChange={handleChange}
              placeholder="z.B. 2–4 ml/L"
              disabled={disabled}
            />
          </div>
          <div className="form-group">
            <label htmlFor="fertilizerApplicationFrequency">Anwendungshäufigkeit</label>
            <select
              id="fertilizerApplicationFrequency"
              name="fertilizerApplicationFrequency"
              value={formData.fertilizerApplicationFrequency}
              onChange={handleChange}
              disabled={disabled}
            >
              <option value="">Bitte wählen</option>
              {applicationFrequencies.map(freq => (
                <option key={freq} value={freq}>{freq}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                name="fertilizerFoliarPossible"
                checked={formData.fertilizerFoliarPossible}
                onChange={handleChange}
                disabled={disabled}
              />
              <span>Blattdüngung möglich</span>
            </label>
          </div>
        </div>
      </fieldset>

      {/* SECTION 5: Preisberechnung */}
      <fieldset className="form-section">
        <legend className="form-section-title">💰 Preisberechnung</legend>
        <div className="form-row">
          <div className="form-group">
            <label htmlFor="fertilizerBottleSizeLiters">Gebindegröße (Liter)</label>
            <input
              type="number"
              id="fertilizerBottleSizeLiters"
              name="fertilizerBottleSizeLiters"
              value={formData.fertilizerBottleSizeLiters}
              onChange={handleChange}
              placeholder="z.B. 1, 5, 0.25"
              step="0.01"
              min="0"
              disabled={disabled}
            />
          </div>
          <div className="form-group">
            <label htmlFor="fertilizerDosagePerLiterNum">Dosierung Gesamt-Mittelwert (ml/L)</label>
            <input
              type="number"
              id="fertilizerDosagePerLiterNum"
              name="fertilizerDosagePerLiterNum"
              value={formData.fertilizerDosagePerLiterNum}
              onChange={handleChange}
              placeholder="Allgemeiner Durchschnitt"
              step="0.001"
              min="0"
              disabled={disabled}
            />
          </div>
        </div>
        <div className="form-row">
          <div className="form-group">
            <label htmlFor="fertilizerDosageVeg">🌱 Dosierung Veg (ml/L)</label>
            <input
              type="number"
              id="fertilizerDosageVeg"
              name="fertilizerDosageVeg"
              value={formData.fertilizerDosageVeg}
              onChange={handleChange}
              placeholder="Durchschnitt Wachstumsphase"
              step="0.001"
              min="0"
              disabled={disabled}
            />
          </div>
          <div className="form-group">
            <label htmlFor="fertilizerDosageBloom">🌸 Dosierung Bloom (ml/L)</label>
            <input
              type="number"
              id="fertilizerDosageBloom"
              name="fertilizerDosageBloom"
              value={formData.fertilizerDosageBloom}
              onChange={handleChange}
              placeholder="Durchschnitt Blütephase"
              step="0.001"
              min="0"
              disabled={disabled}
            />
          </div>
        </div>
        <div className="form-row">
          <div className="form-group">
            <label htmlFor="fertilizerVegSchedule">Veg-Schema (WK-Werte)</label>
            <input
              type="text"
              id="fertilizerVegSchedule"
              name="fertilizerVegSchedule"
              value={formData.fertilizerVegSchedule}
              onChange={handleChange}
              placeholder="z.B. 2,2,2 (WK1,WK2,WK3)"
              disabled={disabled}
            />
          </div>
          <div className="form-group">
            <label htmlFor="fertilizerBloomSchedule">Bloom-Schema (WK-Werte)</label>
            <input
              type="text"
              id="fertilizerBloomSchedule"
              name="fertilizerBloomSchedule"
              value={formData.fertilizerBloomSchedule}
              onChange={handleChange}
              placeholder="z.B. 1,2,2,3,3,4,4,4 (WK1-WK8)"
              disabled={disabled}
            />
          </div>
        </div>
        <div className="form-row">
          <div className="form-group">
            <label htmlFor="fertilizerPricePerBottle">Preis pro Flasche (€)</label>
            <input
              type="number"
              id="fertilizerPricePerBottle"
              name="fertilizerPricePerBottle"
              value={formData.fertilizerPricePerBottle}
              onChange={handleChange}
              placeholder="Wird vom günstigsten Shoppreis berechnet"
              disabled={disabled}
            />
          </div>
          <div className="form-group">
            <label htmlFor="fertilizerPricePerLiter">Preis pro Liter Konzentrat (€/L)</label>
            <input
              type="number"
              id="fertilizerPricePerLiter"
              name="fertilizerPricePerLiter"
              value={formData.fertilizerPricePerLiter}
              onChange={handleChange}
              placeholder="Auto: Preis ÷ Gebindegröße"
              disabled={disabled}
            />
          </div>
          <div className="form-group">
            <label htmlFor="fertilizerSolutionPerEuro">Liter Nährlösung pro € (L/€)</label>
            <input
              type="number"
              id="fertilizerSolutionPerEuro"
              name="fertilizerSolutionPerEuro"
              value={formData.fertilizerSolutionPerEuro}
              onChange={handleChange}
              placeholder="Auto: Gebinde×1000 ÷ (Dosierung×Preis)"
              disabled={disabled}
            />
          </div>
        </div>
      </fieldset>
    </div>
  );
}

export default FertilizerForm;